import { useMemo } from "react";
import { ChevronIcon, DiskIcon, ListIcon, RedoIcon } from "vega-icons";

import { ActionMenu } from "@vega-core/components/ActionMenu/ActionMenu";
import { DropdownMenuTrigger } from "@vega-core/components/shadcn/dropdown-menu";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { useSavedFilters } from "@vega-modules/hooks/useSavedFilters";

import { type FilterState, type TableFilterId } from "../../types";

import { ManageFiltersModal } from "./ManageFiltersModal";
import { SaveFiltersModal } from "./SaveFiltersModal";

export const SaveFilters = ({
  filterState,
  hasActiveFilters,
  resetFilters,
  id,
  applyContextInPersistenceKeys = true,
  disabled = false,
}: {
  filterState: FilterState;
  hasActiveFilters: boolean;
  resetFilters: () => void;
  id: TableFilterId;
  applyContextInPersistenceKeys?: boolean;
  disabled?: boolean;
}) => {
  const { hasFilters, isSaving, isDeleting } = useSavedFilters(
    id,
    applyContextInPersistenceKeys,
  );

  const saveModal = useBoolean(false);
  const manageModal = useBoolean(false);
  const options = useMemo(() => {
    const tempOptions = [];

    if (hasActiveFilters) {
      tempOptions.push({
        icon: <DiskIcon className="text-text/secondary" />,
        label: m.common_save_filters(),
        value: "save",
        onClick: () => {
          saveModal.setIsOpen(true);
        },
      });
      tempOptions.push({
        icon: <RedoIcon className="text-text/secondary" />,
        label: m.common_reset_filters(),
        value: "reset",
        onClick: resetFilters,
      });
    }

    if (hasFilters) {
      tempOptions.push({
        icon: <ListIcon className="text-text/secondary" />,
        label: m.common_manage_filters(),
        value: "manage",
        onClick: () => {
          manageModal.setIsOpen(true);
        },
      });
    }

    return tempOptions;
  }, [hasFilters, saveModal, hasActiveFilters, resetFilters, manageModal]);

  return (
    <div className="flex items-center gap-2">
      <ActionMenu
        isLoading={isSaving || isDeleting}
        menuItems={options.map((option) => ({
          icon: option.icon,
          title: option.label,
          onClick: option.onClick,
        }))}
        renderTrigger={({ isOpen }) => (
          <DropdownMenuTrigger asChild>
            <div
              className={cnTwMerge(
                "flex items-center gap-2 px-2 py-1 defaultTransition rounded-default",
                {
                  "cursor-pointer": !disabled,
                  "cursor-not-allowed opacity-50 pointer-events-none": disabled,
                  "bg-background/component": isOpen && !disabled,
                  "text-text/primary": isOpen && !disabled,
                  "text-text/secondary": !isOpen || disabled,
                },
              )}
            >
              <p className="body-base-semibold">{m.common_filter_actions()}</p>
              <ChevronIcon
                className={cnTwMerge(
                  "h-4 w-4 defaultTransition",
                  isOpen && !disabled && "rotate-180",
                )}
              />
            </div>
          </DropdownMenuTrigger>
        )}
      />
      <SaveFiltersModal
        applyContextInPersistenceKeys={applyContextInPersistenceKeys}
        filterState={filterState}
        id={id}
        saveModal={saveModal}
      />
      <ManageFiltersModal
        applyContextInPersistenceKeys={applyContextInPersistenceKeys}
        id={id}
        manageModal={manageModal}
      />
    </div>
  );
};
